import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@repo/api-client";
import type { Address, Cart } from "@repo/shared-types";
import { buttonStyles, displayTitleStyle, eyebrowStyle, fieldStyle, heroPanelStyle, panelStyle, subtleText, tokens } from "../lib/ui";

export function CheckoutPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cartLoading, setCartLoading] = useState(true);
  const [cart, setCart] = useState<Cart | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState<{ orderId: string; total: string } | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<"stripe" | "paypal" | "cod">("stripe");
  const [address, setAddress] = useState<Address>({
    first_name: "", last_name: "", address1: "",
    city: "Allen", state: "TX", postcode: "", country: "US",
  });

  useEffect(() => {
    api.getCart()
      .then(setCart)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load checkout summary"))
      .finally(() => setCartLoading(false));
  }, []);

  function updateField(field: keyof Address, value: string) {
    setAddress((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!cart?.items?.length) {
      setError("Your cart is empty.");
      return;
    }

    setLoading(true);
    try {
      const order = await api.createOrder({
        shipping_address: address,
        payment_method: paymentMethod,
      });
      setSuccess({ orderId: order.order_id, total: order.total });
      setCart({ ...(cart ?? { id: "", user_id: "", updated_at: new Date().toISOString(), items: [] }), items: [] });
      window.dispatchEvent(new Event("cart:updated"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Checkout failed");
    } finally {
      setLoading(false);
    }
  }

  const items = cart?.items ?? [];
  const total = items.reduce((sum, item) => {
    const price = Number(item.price ?? "0");
    return Number.isFinite(price) ? sum + price * item.quantity : sum;
  }, 0);

  if (cartLoading) {
    return <div className="page-loading">Loading checkout…</div>;
  }

  if (success) {
    return (
      <div className="inner-page__container" style={{ gap: tokens.space.lg, maxWidth: 760 }}>
        <section style={{ ...heroPanelStyle, padding: tokens.space.lg }}>
          <p style={eyebrowStyle}>Order Confirmed</p>
          <h2 style={{ ...displayTitleStyle, margin: "0.35rem 0", fontSize: tokens.fontSize["3xl"] }}>Your order has been placed</h2>
          <p style={subtleText}>We recorded your order and cleared your cart. You can keep browsing or return to the shop.</p>
        </section>

        <section style={{ ...panelStyle, display: "grid", gap: tokens.space.md, background: "linear-gradient(180deg, rgba(255,255,255,0.97), rgba(248,251,255,0.92))" }}>
          <SummaryRow label="Order ID" value={success.orderId} />
          <SummaryRow label="Payment method" value={paymentMethod === "cod" ? "Cash on delivery" : paymentMethod.toUpperCase()} />
          <SummaryRow label="Order total" value={`$${Number(success.total).toFixed(2)}`} />
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            <button type="button" style={buttonStyles.primary} onClick={() => navigate("/shop")}>Back to shop</button>
            <button type="button" style={buttonStyles.secondary} onClick={() => navigate("/cart")}>View cart</button>
          </div>
        </section>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="inner-page__container" style={{ gap: tokens.space.lg, maxWidth: 760 }}>
        <section style={{ ...heroPanelStyle, padding: tokens.space.lg }}>
          <p style={eyebrowStyle}>Order Finalization</p>
          <h2 style={{ ...displayTitleStyle, margin: "0.35rem 0", fontSize: tokens.fontSize["3xl"] }}>Checkout</h2>
          <p style={subtleText}>Add products to your cart before you check out.</p>
        </section>

        <section style={{ ...panelStyle, display: "grid", gap: tokens.space.sm }}>
          <h3 style={{ margin: 0 }}>Your cart is empty</h3>
          <p style={subtleText}>Browse the catalog to add products, then return here to finalize your order.</p>
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            <button type="button" style={buttonStyles.primary} onClick={() => navigate("/shop")}>Browse products</button>
            <button type="button" style={buttonStyles.secondary} onClick={() => navigate("/cart")}>Back to cart</button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="inner-page__container" style={{ gap: tokens.space.lg, maxWidth: 1040 }}>
      <section
        style={{
          ...heroPanelStyle,
          padding: tokens.space.lg,
        }}
      >
        <p style={eyebrowStyle}>Order Finalization</p>
        <h2 style={{ ...displayTitleStyle, margin: "0.35rem 0", fontSize: tokens.fontSize["3xl"] }}>Checkout</h2>
        <p style={subtleText}>Confirm shipping details and choose a payment method to place your order.</p>
      </section>

      {error && (
        <p style={{ color: tokens.color.error, margin: 0, background: tokens.color.errorLight, border: "1px solid #fecaca", borderRadius: tokens.radius.md, padding: "0.8rem 1rem" }}>
          {error}
        </p>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.35fr) minmax(280px, 0.85fr)", gap: tokens.space.md, alignItems: "start" }}>
      <form onSubmit={handleSubmit} style={{ ...panelStyle, display: "grid", gap: tokens.space.md, background: "linear-gradient(180deg, rgba(255,255,255,0.97), rgba(248,251,255,0.92))" }}>
        <h3 style={{ margin: 0 }}>Shipping address</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.75rem" }}>
          <Field label="First name">
            <input
              placeholder="Aarav"
              required
              value={address.first_name}
              onChange={(e) => updateField("first_name", e.target.value)}
              style={fieldStyle}
            />
          </Field>
          <Field label="Last name">
            <input
              placeholder="Sharma"
              required
              value={address.last_name}
              onChange={(e) => updateField("last_name", e.target.value)}
              style={fieldStyle}
            />
          </Field>
          <Field label="Street address">
            <input
              placeholder="123 Main St"
              required
              value={address.address1}
              onChange={(e) => updateField("address1", e.target.value)}
              style={fieldStyle}
            />
          </Field>
          <Field label="City">
            <input
              placeholder="Allen"
              required
              value={address.city}
              onChange={(e) => updateField("city", e.target.value)}
              style={fieldStyle}
            />
          </Field>
          <Field label="State">
            <input
              placeholder="TX"
              required
              maxLength={2}
              value={address.state}
              onChange={(e) => updateField("state", e.target.value.toUpperCase())}
              style={fieldStyle}
            />
          </Field>
          <Field label="ZIP code">
            <input
              placeholder="75002"
              required
              value={address.postcode}
              onChange={(e) => updateField("postcode", e.target.value)}
              style={fieldStyle}
            />
          </Field>
        </div>

        <h3 style={{ margin: "0.35rem 0 0" }}>Payment method</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.65rem" }}>
          {(["stripe", "paypal", "cod"] as const).map((method) => {
            const selected = paymentMethod === method;
            return (
              <button
                key={method}
                type="button"
                onClick={() => setPaymentMethod(method)}
                style={{
                  ...panelStyle,
                  padding: "0.75rem",
                  textAlign: "left",
                  border: selected ? "2px solid #3b82f6" : "1px solid #e5e7eb",
                  background: selected ? "linear-gradient(180deg, #eff6ff, #ffffff)" : "#fff",
                  boxShadow: "none",
                }}
              >
                <div style={{ fontWeight: tokens.fontWeight.semibold }}>
                  {method === "cod" ? "Cash on delivery" : method.toUpperCase()}
                </div>
                <div style={subtleText}>Secure checkout via {method === "cod" ? "offline collection" : method}</div>
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap" }}>
          <button type="button" style={buttonStyles.secondary} onClick={() => navigate("/cart")}>Back to cart</button>
          <button type="submit" disabled={loading} style={buttonStyles.primary}>
            {loading ? "Processing..." : "Place order"}
          </button>
        </div>
      </form>

      <aside style={{ ...panelStyle, display: "grid", gap: "1rem", background: "linear-gradient(180deg, rgba(255,250,240,0.98), rgba(255,255,255,0.95))" }}>
        <div>
          <p style={eyebrowStyle}>Order Summary</p>
          <h3 style={{ margin: "0.35rem 0 0.5rem" }}>Review before payment</h3>
          <p style={subtleText}>Your total comes directly from the worker API and matches the cart snapshot used for order creation.</p>
        </div>

        <div style={{ display: "grid", gap: "0.85rem" }}>
          {items.map((item) => {
            const unitPrice = Number(item.price ?? "0");
            return (
              <div key={item.product_id} style={{ display: "grid", gridTemplateColumns: "56px 1fr auto", gap: "0.75rem", alignItems: "center" }}>
                <div style={checkoutThumbStyle}>
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.name ?? `Product ${item.product_id}`} style={checkoutThumbImageStyle} />
                  ) : (
                    <span aria-hidden="true">🕉️</span>
                  )}
                </div>
                <div>
                  <div style={{ fontWeight: tokens.fontWeight.semibold }}>{item.name ?? `Product #${item.product_id}`}</div>
                  <div style={subtleText}>Qty {item.quantity}</div>
                </div>
                <strong>${Number.isFinite(unitPrice) ? (unitPrice * item.quantity).toFixed(2) : "0.00"}</strong>
              </div>
            );
          })}
        </div>

        <div style={{ borderTop: "1px solid rgba(23,32,51,0.08)", paddingTop: "1rem", display: "grid", gap: "0.6rem" }}>
          <SummaryRow label="Subtotal" value={`$${total.toFixed(2)}`} />
          <SummaryRow label="Shipping" value="Calculated later" />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "0.75rem" }}>
            <span style={{ fontWeight: tokens.fontWeight.semibold }}>Estimated total</span>
            <strong style={{ fontSize: tokens.fontSize["2xl"] }}>${total.toFixed(2)}</strong>
          </div>
        </div>
      </aside>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "grid", gap: "0.35rem" }}>
      <span style={{ fontWeight: tokens.fontWeight.semibold }}>{label}</span>
      {children}
    </label>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", color: "#334155" }}>
      <span>{label}</span>
      <span style={{ fontWeight: tokens.fontWeight.semibold }}>{value}</span>
    </div>
  );
}

const checkoutThumbStyle: React.CSSProperties = {
  width: 56,
  height: 56,
  borderRadius: tokens.radius.md,
  overflow: "hidden",
  background: "linear-gradient(135deg, #fff3d8 0%, #ffe8ba 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const checkoutThumbImageStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
};
